import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import axios from 'axios';
import MUITypography from '../../Components/MUITypography';
import { API_GET_COMPANY_LOGO } from '../../Util/URLConstants';
import StorageFactory from '../../Util/StorageFactory';
import { SESSION_USER_ID_KEY, s3_vm_box_logo } from '../../Util/VMConstants';
import { returnDate } from '../../Util/UtilHelper';
import { VM_Colors } from '../../Styles/VMStyles';

const RATING_OPTIONS = ['Excellent', 'Good', 'Average', 'Poor'];
const DEFAULT_COMPANY_LOGO = s3_vm_box_logo;

const firstEnquiryInfo = (info) => {
  if (!info) return null;
  if (Array.isArray(info)) return info[0] || null;
  if (typeof info === 'object') return info;
  return null;
};

const formatEnquiryDates = (dates) => {
  if (!dates) return '';
  if (typeof dates === 'string') return dates;
  const list = Array.isArray(dates) ? dates : [dates];
  return list.filter(Boolean).map((d) => returnDate(d)).join(', ');
};

const formatEnquiryPhone = (countryCode, phoneNo) => {
  const number = phoneNo ? String(phoneNo).trim() : '';
  if (!number) return '';
  const code = countryCode ? String(countryCode).trim() : '';
  const prefix = code ? (code.startsWith('+') ? code : `+${code}`) : '';
  return [prefix, number].filter(Boolean).join(' ');
};

const displayWebsite = (url) => {
  if (!url) return '';
  return String(url).replace(/^https?:\/\//i, '').replace(/\/$/, '');
};

export const resolveVenueDetails = (venue) => {
  if (!venue || typeof venue !== 'object') return null;

  const location = [
    venue.address,
    venue.city_name,
    venue.state_name,
    venue.country_name,
  ]
    .map((part) => (part ? String(part).trim() : ''))
    .filter(Boolean)
    .join(', ');
  const pincode = venue.pincode ? String(venue.pincode).trim() : '';
  const address = [location, pincode].filter(Boolean).join(' – ');
  const website = venue.website_url ? String(venue.website_url).trim() : '';

  const details = {
    name: venue.venue_name || '',
    type: venue.venue_type || '',
    address,
    phone: formatEnquiryPhone(venue.country_code, venue.venue_mobile_no),
    email: venue.venue_email || '',
    website,
    websiteLabel: displayWebsite(website),
    gst: venue.business_label || venue.business_number || '',
    currency: venue.currency || '',
  };

  if (!Object.values(details).some(Boolean)) return null;
  return details;
};

export const resolveEnquiryDetails = ({
  selectedUsers = [],
  enquiryId = null,
  enquiryInfo = null,
} = {}) => {
  const user = selectedUsers[0] || {};
  const info =
    firstEnquiryInfo(enquiryInfo) ||
    firstEnquiryInfo(user.enquiry_info) ||
    firstEnquiryInfo(user.enquiry_details) ||
    firstEnquiryInfo(user.enquiryInfo) ||
    {};

  const details = {
    name: info.name || user.name || '',
    enquiryId:
      info.appointment_id ||
      info.enquiry_id ||
      user.enquiry_id ||
      enquiryId ||
      '',
    eventDates:
      user.event_dates ||
      formatEnquiryDates(info.appointment_date) ||
      formatEnquiryDates(info.event_dates),
    phone:
      user.phone ||
      formatEnquiryPhone(
        info.country_code || user.country_code,
        info.phone_no || user.phone_no
      ),
    email: info.email || user.email || '',
    address: info.address || user.address || '',
    notes: info.notes || user.notes || '',
  };

  if (!Object.values(details).some(Boolean)) return null;
  return details;
};

const infoShellSx = {
  mb: 3,
  border: '1px solid #E6ECF4',
  borderRadius: 2,
  overflow: 'hidden',
  backgroundColor: '#F8FAFC',
};

const infoSideSx = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gridTemplateRows: { md: 'subgrid' },
  gridRow: { md: '1 / 4' },
  minWidth: 0,
};

const infoHeaderSx = {
  gridColumn: '1 / -1',
  px: { xs: 1.75, sm: 2 },
  py: 1,
  borderBottom: '1px solid #E6ECF4',
  bgcolor: '#F1F5FB',
};

const DetailCell = ({ label, value, showRightBorder, showBottomBorder }) => {
  const text = value == null ? '' : String(value).trim();
  return (
    <Box
      sx={{
        minWidth: 0,
        minHeight: { xs: 64, sm: 72 },
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1.25, sm: 1.5 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        borderRight: showRightBorder ? '1px solid #E6ECF4' : 'none',
        borderBottom: showBottomBorder ? '1px solid #E6ECF4' : 'none',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          height: 16,
          color: VM_Colors.steel_gray,
          fontWeight: 700,
          letterSpacing: 0.7,
          textTransform: 'uppercase',
          fontSize: '0.68rem',
          lineHeight: '16px',
          mb: 0.6,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: text ? '#1A1A1A' : VM_Colors.steel_gray,
          wordBreak: 'break-word',
          lineHeight: 1.45,
          fontSize: '0.875rem',
        }}
      >
        {text || '—'}
      </Typography>
    </Box>
  );
};

const InfoSide = ({ title, items, showDivider }) => (
  <Box
    sx={{
      ...infoSideSx,
      borderRight: showDivider ? { md: '1px solid #E6ECF4' } : 'none',
      borderBottom: { xs: '1px solid #E6ECF4', md: 'none' },
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Box sx={infoHeaderSx}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: VM_Colors.primary,
          fontWeight: 800,
          letterSpacing: 1.1,
          textTransform: 'uppercase',
          fontSize: '0.7rem',
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
    </Box>
    {items.map((item, index) => (
      <DetailCell
        key={item.label}
        label={item.label}
        value={item.value}
        showRightBorder={index % 2 === 0}
        showBottomBorder={index < 2}
      />
    ))}
  </Box>
);

export function VenueDetailsPanel({ venue = null }) {
  const details = resolveVenueDetails(venue);
  if (!details) return null;

  return (
    <Box sx={infoShellSx}>
      <InfoSide
        title="Venue"
        items={[
          { label: 'Venue name', value: details.name },
          { label: 'Mobile', value: details.phone },
          { label: 'Email', value: details.email },
          { label: 'Address', value: details.address },
        ]}
      />
    </Box>
  );
}

export function EnquiryDetailsPanel({
  selectedUsers = [],
  enquiryId = null,
  enquiryInfo = null,
  venue = null,
}) {
  const details = resolveEnquiryDetails({ selectedUsers, enquiryId, enquiryInfo });
  const venueDetails = resolveVenueDetails(venue);

  if (!details && !venueDetails) return null;

  return (
    <Box
      sx={{
        ...infoShellSx,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gridTemplateRows: { md: 'auto 1fr 1fr' },
        alignItems: 'stretch',
      }}
    >
      {venueDetails && (
        <InfoSide
          title="Venue"
          showDivider={Boolean(details)}
          items={[
            { label: 'Venue name', value: venueDetails.name },
            { label: 'Mobile', value: venueDetails.phone },
            { label: 'Email', value: venueDetails.email },
            { label: 'Address', value: venueDetails.address },
          ]}
        />
      )}
      {details && (
        <InfoSide
          title="Client"
          items={[
            { label: 'Client', value: details.name },
            { label: 'Phone', value: details.phone },
            { label: 'Event date', value: details.eventDates },
            { label: 'Enquiry ID', value: details.enquiryId },
          ]}
        />
      )}
    </Box>
  );
}

function FeedbackTemplate({
  allCategories = [],
  selectedUsers = [],
  sociallinks = [],
  onSubmit,
  enquiryId = null,
  enquiryInfo = null,
  venue = null,
  userId: userIdProp,
}) {
  const [formData, setFormData] = useState({});
  const [remarks, setRemarks] = useState('');
  const [errors, setErrors] = useState({});
  const [submitMsg, setSubmitMsg] = useState('');
  const [referOption, setReferOption] = useState('');
  const [referrals, setReferrals] = useState([{ name: '', email: '', phone: '+91' }]);
  const [referralErrors, setReferralErrors] = useState([]);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [logoLoading, setLogoLoading] = useState(true);

  const sessionFactory = useMemo(() => new StorageFactory('session'), []);
  const userId = userIdProp || sessionFactory.get(SESSION_USER_ID_KEY);

  useEffect(() => {
    if (!userId) {
      setCompanyLogo(DEFAULT_COMPANY_LOGO);
      setLogoLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchLogo = async () => {
      setLogoLoading(true);
      setCompanyLogo(null);
      setLogoError(false);
      try {
        const params = { user_id: userId };
        if (enquiryId) params.enquiry_id = enquiryId;

        const res = await axios.get(API_GET_COMPANY_LOGO, { params });
        if (cancelled) return;

        if (res?.data?.status_code === 200) {
          const apiLogo = res.data.data?.company_logo;
          setCompanyLogo(apiLogo?.trim() ? apiLogo : DEFAULT_COMPANY_LOGO);
        } else {
          setCompanyLogo(DEFAULT_COMPANY_LOGO);
          setLogoLoading(false);
        }
        setLogoError(false);
      } catch (err) {
        console.error('Failed to fetch company logo:', err);
        if (!cancelled) {
          setCompanyLogo(DEFAULT_COMPANY_LOGO);
          setLogoLoading(false);
        }
      }
    };

    fetchLogo();
    return () => {
      cancelled = true;
    };
  }, [userId, enquiryId]);

  const logoSrc =
    companyLogo && !logoError ? companyLogo : DEFAULT_COMPANY_LOGO;

  // === GLOBAL DISABLE SWITCH ===
  const DISABLED = true;

  // ----- URL helpers: normalize then validate -----
  const normalizeUrl = (raw) => {
    let u = String(raw || '').trim();
    if (!u) return '';
    u = u.replace(/^https:([^/])/, 'https://$1').replace(/^http:([^/])/, 'http://$1');
    if (!/^https?:\/\//i.test(u)) u = 'https://' + u;
    return u;
  };

  const isValidUrl = (url) => {
    try {
      const u = new URL(url);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // ----- Data prep -----
  const uniqueCategories = useMemo(() => {
    const cleaned = (allCategories || [])
      .map((c) => (typeof c === 'string' ? c.trim() : ''))
      .filter(Boolean);
    return [...new Set(cleaned)];
  }, [allCategories]);

  const rateUsLinks = useMemo(() => {
    if (!Array.isArray(sociallinks)) return [];
    return sociallinks
      .map((s) => {
        const normalized = normalizeUrl(s?.url);
        return {
          url: normalized,
          isValid: isValidUrl(normalized),
          label:
            s?.platform === 'Other' && s?.customPlatform
              ? s.customPlatform
              : s?.platform || 'Link',
        };
      })
      .filter((s) => s.isValid);
  }, [sociallinks]);

  // ----- Handlers (kept; won’t fire because card is non-interactive) -----
  const handleRating = useCallback((category, value) => {
    if (!value) return;
    setFormData((prev) => ({ ...prev, [category]: value }));
    setErrors((prev) => ({ ...prev, [category]: '' }));
    setSubmitMsg('');
  }, []);

  const handleReferralChange = (index, field, value) => {
    setReferrals((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setReferralErrors((prev) => {
      const next = [...prev];
      if (next[index]) next[index][field] = '';
      return next;
    });
  };

  const addReferral = () => {
    setReferrals((prev) => [...prev, { name: '', email: '', phone: '+91' }]);
    setReferralErrors((prev) => [...prev, {}]);
  };

  const removeReferral = (index) => {
    setReferrals((prev) => prev.filter((_, i) => i !== index));
    setReferralErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = useCallback(() => {
    const nextErrors = {};
    uniqueCategories.forEach((cat) => {
      if (!formData[cat]) nextErrors[cat] = 'Please select a rating';
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setSubmitMsg('Please complete all required ratings.');
      return false;
    }
    setSubmitMsg('');
    return true;
  }, [formData, uniqueCategories]);

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = {
      responses: formData,
      remarks,
      users: selectedUsers,
      sociallinks,
    };
    if (typeof onSubmit === 'function') onSubmit(payload);
    else console.log('Submitting feedback:', payload);
  };

  // ----- UI -----
  return (
    <Box sx={{ mx: 'auto', width: '100%', maxWidth: 880, p: { xs: 1, sm: 2 } }}>
      <Card
        elevation={2}
        aria-disabled={DISABLED ? 'true' : 'false'}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          pointerEvents: DISABLED ? 'none' : 'auto',
          userSelect: DISABLED ? 'none' : 'auto',
          opacity: DISABLED ? 0.6 : 1,
          filter: DISABLED ? 'grayscale(10%)' : 'none',
        }}
      >
        <CardHeader
          sx={{
            pb: 1.5,
            pt: 2,
            px: { xs: 2, sm: 3 },
            alignItems: 'center',
            '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' },
            '& .MuiCardHeader-content': { overflow: 'hidden', pr: 2 },
          }}
          action={
            <Box
              sx={{
                position: 'relative',
                width: { xs: 72, sm: 96 },
                height: { xs: 48, sm: 56 },
                borderRadius: 1.5,
                bgcolor: '#f5f5f5',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              {companyLogo && (
                <Avatar
                  src={logoSrc}
                  alt="Company logo"
                  variant="rounded"
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 1.5,
                    bgcolor: 'transparent',
                    opacity: logoLoading ? 0 : 1,
                    transition: 'opacity 0.2s ease',
                    '& img': { objectFit: 'contain' },
                  }}
                  imgProps={{
                    onLoad: () => setLogoLoading(false),
                    onError: () => {
                      setLogoError(true);
                      setLogoLoading(false);
                    },
                  }}
                />
              )}
              {logoLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.85)',
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          }
          title={
            <MUITypography variant="h5" fontWeight={700}>
              Share Your Feedback
            </MUITypography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              We appreciate your time—your input helps us improve every event.
            </Typography>
          }
        />

        <Divider />

        <CardContent sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 3 }}>
          <EnquiryDetailsPanel
            selectedUsers={selectedUsers}
            enquiryId={enquiryId}
            enquiryInfo={enquiryInfo}
            venue={venue}
          />

          {/* Categories */}
          {uniqueCategories.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
              No categories available.
            </Typography>
          ) : (
            <Stack spacing={3} mt={1}>
              {uniqueCategories.map((category) => {
                const value = formData[category] || '';
                const hasError = Boolean(errors[category]);
                return (
                  <Box key={category}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1 }}
                    >
                      <MUITypography sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
                        {category}
                      </MUITypography>
                      {hasError && (
                        <Typography variant="caption" color="error">
                          {errors[category]}
                        </Typography>
                      )}
                    </Stack>

                    <ToggleButtonGroup
                      exclusive
                      value={value}
                      onChange={(_, v) => handleRating(category, v)}
                      sx={{ flexWrap: 'wrap', gap: 1 }}
                    >
                      {RATING_OPTIONS.map((opt) => (
                        <ToggleButton
                          key={opt}
                          value={opt}
                          size="small"
                          sx={{
                            px: 2.2,
                            py: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                          }}
                        >
                          {opt}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                );
              })}
            </Stack>
          )}

          {/* Remarks */}
          <Box sx={{ mt: 4 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Kindly share your overall experience"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </Box>

          {/* Refer section */}
          <Box sx={{ mt: 4 }}>
            <MUITypography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              Would you like to refer?
            </MUITypography>
            <RadioGroup
              row
              value={referOption}
              onChange={(e) => setReferOption(e.target.value)}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>

            {referOption === 'yes' && (
              <Stack spacing={2} mt={2}>
                {referrals.map((ref, idx) => (
                  <Grid
                    key={idx}
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1 }}
                  >
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={ref.name}
                        onChange={(e) => handleReferralChange(idx, 'name', e.target.value)}
                        error={!!referralErrors[idx]?.name}
                        helperText={referralErrors[idx]?.name}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={ref.email}
                        onChange={(e) => handleReferralChange(idx, 'email', e.target.value)}
                        error={!!referralErrors[idx]?.email}
                        helperText={referralErrors[idx]?.email}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={ref.phone}
                        onChange={(e) => handleReferralChange(idx, 'phone', e.target.value)}
                        error={!!referralErrors[idx]?.phone}
                        helperText={referralErrors[idx]?.phone}
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton
                        onClick={() => removeReferral(idx)}
                        disabled={referrals.length === 1}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}

                <Box display="flex" justifyContent="flex-end">
                  <Button variant="outlined" onClick={addReferral}>
                    Add Referral
                  </Button>
                </Box>
              </Stack>
            )}
          </Box>

          {/* Rate US (social links) */}
          {rateUsLinks.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <MUITypography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                Rate US
              </MUITypography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                {rateUsLinks.map((item, idx) => (
                  <Box
                    key={`${item.url}-${idx}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, minWidth: 160 }}>
                      {item.label}
                    </Typography>
                    <Button
                      variant="text"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none', wordBreak: 'break-all', px: 0 }}
                    >
                      {item.url}
                    </Button>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Form message */}
          {submitMsg && (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 3 }}>
              {submitMsg}
            </Typography>
          )}

          {/* Submit */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="contained" size="large" onClick={handleSubmit}>
              Submit Feedback
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default FeedbackTemplate;
